import { jitsiLocalStorage } from '@jitsi/js-utils';
import _ from 'lodash';
import moment from 'moment';
import React, { PureComponent } from 'react';
import type { Dispatch } from 'redux';

import { styleDeeplinking } from '../../../../deep-linking/components/styleWeb';
import {
    DeviceSelection,
    getDeviceSelectionDialogProps
} from '../../../../device-selection';
import SelectDevices from '../../../../device-selection/components/SelectDevices';
import {
    _getDefaultPhoneNumber,
    isSharingEnabled,
    sharingFeatures
} from '../../../../invite';
import DialInSection from '../../../../invite/components/add-people-dialog/web/DialInSection';
import { StartSound as startSound } from '../../../../lobby/actions.web';
import { joinConference } from '../../../../prejoin';
import { Footer } from '../../../../welcome/components/Footer';
import { Header } from '../../../../welcome/components/Header';
import ConferenceInfoController from '../../../conference/ConferenceInfoController';
import { fetchBackendConferenceInfo } from '../../../conference/conferenceInfoBackend';
import { watchConferenceInfo } from '../../../conference/conferenceInfoWatch';
import { hasConferenceStarted } from '../../../conference/roomInfo';
import { getToolbarButtons } from '../../../config';
import { getWindowWidth } from '../../../environment/utils';
import { translate } from '../../../i18n';
import {
    AudioOffMute,
    AudioOn,
    Icon,
    IconExpandLess,
    IconExpandMore,
    IconNotAllowed
} from '../../../icons';
import { connect } from '../../../redux';
import { getBrandingColorPalette } from '../../../styles';
import { getBackendSafeRoomName } from '../../../util';
import GetMediaQuery from '../../../util/GetMediaQuery';
import { allowUrlSharing } from '../../functions';

import { ClaimMeetingDialog } from './ClaimMeetingDialog';
import ConnectionStatus from './ConnectionStatus';
import CopyMeetingUrl from './CopyMeetingUrl';
import Preview from './Preview';
import { styleWeb } from './style';

declare var interfaceConfig: Object;
declare var config: Object;

/**
 * How often to poll when neither push nor a live XMPP session is available. This
 * is the join gate for a waiting guest, so it stays tight: on this path the
 * period is the guest's join latency.
 */
const CONFERENCE_STATE_FALLBACK_INTERVAL = 3000;

const stylePremeet = {
    width552: { width: '170px' },
    margintop558: { marginTop: '12px' },
    floatRight: {
        float: 'right',
        cursor: 'pointer'
    }
};

type Props = {

    /**
     * The Redux dispatch function.
     */
    dispatch: Dispatch<any>,

    /**
     * AccessToken of the application.
     */
    _accessToken: ?string,

    /**
     * Children component(s) to be rendered on the screen.
     */
    children: React$Node,

    /**
     * Footer to be rendered for the page (if any).
     */
    footer?: React$Node,

    /**
     * The name of the participant.
     */
    name?: string,

    /**
     * Indicates whether the avatar should be shown when video is off.
     */
    showAvatar: boolean,

    /**
     * Conference.
     */
    conference?: Object,

    /**
     * Guest user not allowed to view this conference.
     */
    guestAllowed: boolean,

    /**
     * Jwt.
     */
    _jwt?: Object,

    /**
     * IsLobby screen enabled.
     */
    isLobby?: Boolean,

    /**
     * Indicates whether the label and copy url action should be shown.
     */
    showConferenceInfo: boolean,

    /**
     * Loading message.
     */
    isLoadingMessage: string,

    /**
     * Local Participant details.
     */
    _localParticipant: ?Object,

    /**
     * Did the person join before the host.
     */
    isPrejoin: boolean,

    /**
     * Title of the screen.
     */
    title: string,

    /**
     * The 'Skip prejoin' button to be rendered (if any).
     */
    skipPrejoinButton?: React$Node,

    /**
     * DIal in section 'render' of the page.
     */
    _dialInVisible: ?boolean, //

    /**
     * Phone render or not.
     */
    _phoneNumber: ?string,

    /**
     * Room name .
     */
    roomName: ?string,

    /**
     * User Details.
     */
    _userDetails: ?string,

    /*
     *
     */
    virtualBackgroundDialog?: React$Node,

    /**
     * True if the preview overlay should be muted, false otherwise.
     */
    videoMuted?: boolean,

    /**
     * New Text.
     */
    newText: ?string,

    /**
     * Pre registration data.
     */
    pregData: ?Object,

    /**
     * The video track to render as preview (if omitted, the default local track will be rendered).
     */
    videoTrack?: Object,

    /**
     * JwtStored when the user is not a moderator.
     */
    _jwtStored: ?Object,

    /**
     * Lobby screen.
     */
    _lobbyScreen: ?Object,

    _playSound: Function,

    _stopSound: Function,

    _registerSound: Function,
    _soundBool: boolean,
    _joinConference: Function,
};

type State = {
    onAccordian: boolean,
    audioState: boolean,
};

/**
 * Implements a pre-meeting screen that can be used at various pre-meeting phases, for example
 * on the prejoin screen (pre-connection) or lobby (post-connection).
 */
class PreMeetingScreen extends PureComponent<Props, State> {
    /**
   * Default values for {@code Prejoin} component's properties.
   *
   * @static
   */
    static defaultProps = {
        showAvatar: true,
        showConferenceInfo: true,
        width: getWindowWidth()
    };

    /**
   * Initializes a new {@code PreMeetingScreen} instance.
   *
   * @inheritdoc
   */
    constructor(props) {
        super(props);

        this.state = {
            onAccordian: false,
            audioState: props?._lobbyScreen?._isLobbyScreenVisible ?? false,
            participantCount: 0,
            conferenceStarted: false,
            hideOptions: true
        };

        // Live conference state. The subscription, its fallbacks and its teardown
        // all live in the controller; this screen only starts and stops it.
        this._conferenceInfo = new ConferenceInfoController(
            props._watchConferenceInfo,
            info => this.setState({
                participantCount: info.participants,
                conferenceStarted: hasConferenceStarted(info)
            }),
            {
                fallbackInterval: CONFERENCE_STATE_FALLBACK_INTERVAL,
                backendFallback: () => fetchBackendConferenceInfo({
                    roomName: props.roomName,
                    accessToken: props._accessToken
                })
            }
        );

        this._onAccordian = this._onAccordian.bind(this);
        this._onChangeIconState = this._onChangeIconState.bind(this);
        this._renderClaimMeeting = this._renderClaimMeeting.bind(this);
        this._renderSelectedDevice = this._renderSelectedDevice(this);
    }

    /**
   * Starts watching when the screen mounts already showing the lobby.
   *
   * @inheritdoc
   * @returns {void}
   */
    componentDidMount() {
        const { _lobbyScreen } = this.props;

        if (_lobbyScreen?._isPrejoin && _lobbyScreen?._isLobbyScreenVisible) {
            this._conferenceInfo.start(this.props.roomName);
        }
    }

    /*
     * Legacy, before the Prosody conference-info watch: polled the Meet Hour API
     * (meeting/totalparticipants) every 8s through <ReactInterval> in render() and
     * joined once the count reached 1. Kept for reference only; that API call now
     * lives on as the last-resort fallback in conferenceInfoBackend.js.
     *
     * constructor:
     *     this.someInterval = this.someInterval.bind(this);
     *     this.intervalRef = React.createRef(null);
     *
     * async someInterval() {
     *     const { roomName: _roomName } = this.props;
     *
     *     const moement = moment().seconds(0)
     *         .milliseconds(0)
     *         .format('X');
     *
     *     const mesh256 = sha256(`${apiKEY}:MH:${moement}`);
     *     const roomName = {
     *         client_id: config.MH_CLIENT_ID,
     *         credentials: mesh256,
     *         domain: mtDomain,
     *
     *         meeting_id: _roomName
     *     };
     *
     *     const userAccessToken
     *         = jitsiLocalStorage.getItem('accessToken') ?? this.props._accessToken;
     *
     *     const headers = _.isNil(userAccessToken)
     *         ? {
     *             'Content-Type': 'application/json'
     *         }
     *         : {
     *             'Content-Type': 'application/json',
     *             Authorization: `Bearer ${userAccessToken}`
     *         };
     *
     *     const participantCountObject = await fetchAxiosApi(
     *         'meeting/totalparticipants',
     *         {
     *             method: 'post',
     *             headers,
     *             body: JSON.stringify(roomName)
     *         }
     *     );
     *
     *     const participantCount = participantCountObject?.data?.total_participant
     *         ? Number(participantCountObject?.data?.total_participant)
     *         : 0;
     *     const { allow_to_join = false } = participantCountObject?.data ?? {};
     *
     *     if (this.state.participantCount === 0 && participantCount === 1) {
     *         await sleep(3000);
     *     }
     *
     *     this.setState(prevState => {
     *         return {
     *             ...prevState,
     *             participantCount,
     *             allow_to_join
     *         };
     *     });
     * }
     *
     * componentDidUpdate(prevProps, prevState) {
     *     const { _lobbyScreen } = this.props;
     *     const _isLobbyScreenVisible = _lobbyScreen?._isLobbyScreenVisible
     *         ? _lobbyScreen?._isLobbyScreenVisible
     *         : null;
     *     const _isPrejoin = _lobbyScreen?._isPrejoin
     *         ? _lobbyScreen?._isPrejoin
     *         : null;
     *
     *     if (
     *         _isPrejoin
     *         && _isLobbyScreenVisible
     *         && prevState.participantCount !== this.state.participantCount
     *     ) {
     *         if (this.state.participantCount >= 1) {
     *             typeof this.intervalRef.current.stop === 'function'
     *                 && this.intervalRef.current.stop();
     *             this.props._joinConference();
     *         }
     *     }
     * }
     */

    /**
   * Starts and stops the conference watch as the waiting screen comes and goes,
   * and joins once the meeting has started.
   *
   * @inheritdoc
   * @returns {void}
   */
    componentDidUpdate(prevProps, prevState) {
        const { _lobbyScreen } = this.props;
        const _isLobbyScreenVisible = Boolean(_lobbyScreen?._isLobbyScreenVisible);
        const _isPrejoin = Boolean(_lobbyScreen?._isPrejoin);
        const shouldWatch = _isPrejoin && _isLobbyScreenVisible;

        if (shouldWatch) {
            this._conferenceInfo.start(this.props.roomName);
        } else {
            this._conferenceInfo.stop();
        }

        if (
            shouldWatch
            && prevState.conferenceStarted !== this.state.conferenceStarted
            && this.state.conferenceStarted
        ) {
            this._conferenceInfo.stop();
            this.props._joinConference();
        }
    }

    /**
   * Shows additional content in WelcomePage.
   *
   * @inheritdoc
   * @returns {void}
   */
    componentWillUnmount() {
        const removeAdditionalContent = document.getElementById(
            'welcome-page-additional-card-template'
        );

        if (removeAdditionalContent) {
            removeAdditionalContent.innerHTML = '';
        }

        if (document.body) {
            document.body.classList.remove('welcome-page');
        }

        this._conferenceInfo.stop();
    }

    onMobileResponsive = () => {
        if (interfaceConfig?.applyMeetingSettings === true) {
            document.body.style.setProperty(
                'background-color',
                getBrandingColorPalette(interfaceConfig)?.background,
                'important'
            );
        }
    };

    _onChangeIconState: () => void;

    /**
   * Cotrols the 'Dialog'.
   *
   * @private
   * @returns {void}
   */
    _onChangeIconState() {
        if (this.props._soundBool) {
            this.props._playSound(!this.props._soundBool);
        } else {
            this.props._playSound(!this.props._soundBool);
        }
    }

    /**
   * Renders the selected device.
   *
   * @private
   * @returns {void}
   */
    _renderSelectedDevice() {
        if (
            this.props._deviceSelectionProps.availableDevices.videoInput.length > 0
        ) {
            return <DeviceSelection props = { this.props._deviceSelectionProps } />;
        }

        return (
            <Preview
                name = { this.props.name }
                showAvatar = { this.props.showAvatar }
                videoMuted = { this.props.videoMuted }
                videoTrack = { this.props.videoTrack } />
        );
    }

    _onAccordian: () => void;

    /**
   * Cotrols the 'Dialog'.
   *
   * @private
   * @returns {void}
   */
    _onAccordian() {
        this.setState(prevState => {
            return {
                ...prevState,
                onAccordian: !prevState.onAccordian
            };
        });
    }

    toggleHideOptions = () => {
        this.setState(prevState => {
            return {
                hideOptions: !prevState.hideOptions
            };
        });
    };
    _renderClaimMeeting: () => React$Node;

    /**
   * Cotrols the 'Dialog'.
   *
   * @private
   * @returns {void}
   */
    _renderClaimMeeting() {
        return <ClaimMeetingDialog />;
    }

    /**
   * Implements {@code PureComponent#render}.
   *
   * @inheritdoc
   */
    render() {
        const {
            name,
            showAvatar,
            showConferenceInfo,
            videoMuted,
            videoTrack,
            _phoneNumber,
            _dialInVisible,
            _jwt,
            _jwtStored,
            _toolbarButtons,
            isLoadingMessage,
            _accessToken,
            pregData,
            _lobbyScreen,
            t,
            virtualBackgroundDialog
        } = this.props;
        const showSharingButton = allowUrlSharing();
        const _visibleButtons = new Set(_toolbarButtons);
        const accessTkn = _accessToken ?? jitsiLocalStorage.getItem('accessToken');
        const _isLobbyScreenVisible = _lobbyScreen?._isLobbyScreenVisible
            ? _lobbyScreen?._isLobbyScreenVisible
            : null;
        const _isPrejoin = _lobbyScreen?._isPrejoin
            ? _lobbyScreen?._isPrejoin
            : null;
        let meetingTime;
        const meetingDate = pregData?.meeting_start_time;

        if (meetingDate) {
            meetingTime = moment(pregData?.meeting_start_time)
                .locale('en-gb')
                .format('DD MMM YYYY - h:mm A');
        }
        const h4Meeting = {
            ...styleWeb.meetingTime,
            textAlign: 'start'
        };

        const preMeetingStyle
            = interfaceConfig?.applyMeetingSettings === true
                ? { background: getBrandingColorPalette(interfaceConfig)?.background }
                : styleWeb.premeetingScreen;

        const invalidMeetingStyle = {
            ...styleWeb.alignCenter,
            background: getBrandingColorPalette(interfaceConfig)?.primary
        };

        const panelTile1Style = {
            ...styleWeb.panelTitle1,
            background: window.innerWidth <= 575 ? getBrandingColorPalette(interfaceConfig)?.primary : ''
        };

        const previewLogConferenceSettingsStyle = {
            display: this.state.hideOptions ? 'none' : 'block',
            width: this.state.hideOptions ? '0' : '100%'
        };

        const invalidMeetingTextStyle = {
            ...styleWeb.h4Style,
            color: interfaceConfig.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig)?.accent1 : '#fff'
        };

        const noInviteClass = ((typeof config.disableInviteFunctions === 'undefined'
            && config.disableInviteFunctions === false) || _visibleButtons.has('invite')) || window.innerWidth < 772 ? '' : 'no-invite';


        return (
            <div
                className = 'premeeting-screen'
                id = 'lobby-screen'
                style = { preMeetingStyle }>
                {/* <Watermarks /> */}
                {/* Legacy API poll, see someInterval above:
                {_isPrejoin && _isLobbyScreenVisible && (
                    <ReactInterval
                        callback = { this.someInterval }
                        enabled = { _isPrejoin && _isLobbyScreenVisible }
                        ref = { this.intervalRef }
                        timeout = { 8000 } />
                )} */}
                {!videoMuted && <div className = 'preview-overlay' />}
                <GetMediaQuery isMobile = { this.onMobileResponsive } />
                <Header prejoin = { true } />

                <ConnectionStatus />
                <div
                    className = { 'pre-join-preset-container' }
                    style = { styleWeb.containers }>
                    <div className = { `rows ${noInviteClass}` }>
                        {pregData
                            && pregData.success
                            && !pregData.claim
                            && (pregData.allow_guest === 1 || _jwt || _jwtStored)
                            && !_isLobbyScreenVisible && (
                            <>
                                <div className = 'col-m-12 preview-container'>
                                    <div className = 'preview-log-conference'>
                                        <Preview
                                            hideOptions = { this.state.hideOptions }
                                            interfaceConfig = { interfaceConfig }
                                            name = { name }
                                            showAvatar = { showAvatar }
                                            toggleHideOptions = { this.toggleHideOptions }
                                            videoMuted = { videoMuted }
                                            videoTrack = { videoTrack }
                                            virtualBackgroundDialog = { virtualBackgroundDialog } />

                                        <div
                                            className = 'preview-log-conference-settings'
                                            style = { previewLogConferenceSettingsStyle }>
                                            <SelectDevices { ...this.props._deviceSelectionProps } />
                                        </div>

                                        {/* {this._renderSelectedDevice} */}
                                        {/* <DeviceSelection {...this.props._deviceSelectionProps} /> */}
                                        {/* <AudioInputPreview /> */}
                                    </div>
                                </div>

                                <div
                                    className =
                                        { `col-m-12 prejoin-meeting-details ${noInviteClass}` }>
                                    { ((typeof config.disableInviteFunctions === 'undefined' && config.disableInviteFunctions === false) || _visibleButtons.has('invite')) && (
                                        <>
                                            { <div className = 'meeting-ready-prejoin'>
                                                <div className = 'text-center'>
                                                    <h4 style = { styleWeb.alignh4 }>
                                                        {this.props._meetingName ? this.props._meetingName : t('welcomepage.meetingIsReady') }
                                                    </h4>
                                                </div>

                                                <div className = 'meeting-link-desktop text-center'>
                                                    {showConferenceInfo && <CopyMeetingUrl />}
                                                    {pregData.meeting_start_time
                                                            && pregData.timezone && (
                                                        <>
                                                            <h4 style = { h4Meeting }>
                                                                <strong>
                                                                    {t('welcomepage.meetingDate')}:{' '}
                                                                </strong>
                                                                {meetingTime}
                                                            </h4>
                                                            <h4 style = { h4Meeting }>
                                                                <strong>
                                                                    {t('welcomepage.timezone')}:{'    '}
                                                                </strong>
                                                                {pregData.timezone}
                                                            </h4>
                                                        </>
                                                    )}
                                                    {_phoneNumber && _dialInVisible
                                                            && <DialInSection phoneNumber = { _phoneNumber } />
                                                    }
                                                </div>

                                                <div className = 'meeting-link-mobile text-center'>
                                                    <div className = 'wrapper center-block'>
                                                        <div
                                                            aria-multiselectable = 'true'
                                                            className = 'panel-group'
                                                            id = 'accordion'
                                                            role = 'tablist'>
                                                            <div className = 'panel panel-default'>
                                                                <div
                                                                    className = { `panel-heading
                                                ${this.state.onAccordian
                                            ? 'active'
                                            : ''
                                        }` }
                                                                    id = 'headingOne'
                                                                    role = 'tab'>
                                                                    <h4
                                                                        className = 'panel-title'
                                                                        onClick = { this._onAccordian }>
                                                                        <a
                                                                            aria-controls = 'collapseOne'
                                                                            aria-expanded = 'true'
                                                                            className = 'panel-title1'
                                                                            data-parent = '#accordion'
                                                                            data-toggle = 'collapse'
                                                                            role = 'button'
                                                                            style = { panelTile1Style }>
                                                                            <div style = { styleWeb.panelTitlediv }>
                                                                                {t('welcomepage.meetingDetails')}
                                                                            </div>
                                                                            <Icon
                                                                                color = '#000000'
                                                                                src = {
                                                                                    this.state.onAccordian
                                                                                        ? IconExpandLess
                                                                                        : IconExpandMore
                                                                                } />
                                                                        </a>
                                                                    </h4>
                                                                </div>
                                                                <div
                                                                    aria-labelledby = 'headingOne'
                                                                    className = { `panel-collapse collapse in
                                                ${this.state.onAccordian
                                            ? 'show'
                                            : ''
                                        }` }
                                                                    id = 'collapseOne'
                                                                    role = 'tabpanel'>
                                                                    <div
                                                                        className = 'panel-body'
                                                                        style = { styleWeb.panelBody }>
                                                                        {showSharingButton
                                                                            ? <CopyMeetingUrl />
                                                                            : null}
                                                                        {pregData.meeting_start_time
                                                                                && pregData.timezone && (
                                                                            <>
                                                                                <h4 style = { h4Meeting }>
                                                                                    {t('welcomepage.meetingDate')}: {' '}
                                                                                    {meetingTime}
                                                                                </h4>
                                                                                <h4 style = { h4Meeting }>
                                                                                    {t('welcomepage.timezone')}:{' '}
                                                                                    {pregData.timezone}
                                                                                </h4>
                                                                            </>
                                                                        )}
                                                                        {/* <a href = '#'>
                                                        <i
                                                            className = 'bx bx-copy'
                                                            style = { styleWeb.conferenceLink } /></a> */}
                                                                        {_phoneNumber && _dialInVisible && (
                                                                            <>
                                                                                <hr />
                                                                                <DialInSection
                                                                                    phoneNumber = { _phoneNumber } />
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>}
                                        </>
                                    )}
                                    <div className = { `ready-to-join ${noInviteClass}` }>{this.props.children}</div>
                                </div>
                            </>
                        )}

                        {

                            pregData
                                && (pregData?.success === false || pregData?.allow_guest === 0)
                                && !pregData?.claim
                                && !_jwt
                                && !_jwtStored
                                && !_isLobbyScreenVisible ? (
                                    <>
                                        <div className = 'col-md-12 col-sm-12'>
                                            <br />
                                            <br />
                                            <div
                                                className = 'invalidMeeting text-center'
                                                style = { invalidMeetingStyle }>
                                                {_.isEmpty(isLoadingMessage)
                                                    ? pregData.success && pregData.allow_guest === 0 ? (
                                                        <img
                                                            className = 'img-fluid'
                                                            src = 'images/privacy.png'
                                                            style = { styleWeb.loadingImg } />
                                                    )

                                                        : <Icon
                                                            className = 'img-fluid'
                                                            src = { IconNotAllowed } />

                                                    : (
                                                        <img
                                                            className = 'img-fluid'
                                                            src = 'images/loading2.gif'
                                                            style = { styleWeb.loadingImg } />
                                                    )}

                                                <h4 style = { invalidMeetingTextStyle }>
                                                    {_.isEmpty(isLoadingMessage)
                                                        ? pregData
                                                        && (pregData.allow_guest === 0 || pregData.success)
                                                            ? t('welcomepage.privateMeetingErr')
                                                            : pregData.code === 403
                                                                ? t('welcomepage.oopsDeviceClockorTimezoneErr')
                                                                : pregData.code === 1002 ? t('welcomepage.invalidMeetingID')
                                                                    : pregData.code === 1351 ? t('welcomepage.monthlyLimitReached')
                                                                        : pregData.code === 1352 ? t('welcomepage.meetingExpire') : pregData.message
                                                        : isLoadingMessage}
                                                </h4>
                                            </div>
                                            <br />
                                            <br />
                                        </div>
                                    </>
                                ) : null
                        }
                        {pregData
                            && pregData.success
                            && pregData.claim === true
                            && !_jwt
                            && !_jwtStored
                            && !_isLobbyScreenVisible && (
                            <>
                                <div className = 'col-d-12 col-sm-12'>
                                    <br />
                                    <br />
                                    <div
                                        className = 'invalidMeeting'
                                        style = { invalidMeetingStyle }>
                                        {accessTkn
                                            ? this._renderClaimMeeting()
                                            : (
                                                <div className = 'text-center'>
                                                    <img
                                                        className = 'img-fluid'
                                                        src = 'images/Upgrade-2.0.png'
                                                        style = { stylePremeet.width552 } />
                                                    <h4 style = { invalidMeetingTextStyle }>
                                                        {t('prejoin.systemUpgradedInformation')}
                                                    </h4>

                                                    <ul
                                                        className = 'list-unstyled'
                                                        style = { stylePremeet.margintop558 }>
                                                        <li className = 'signin-li'>
                                                            <a
                                                                href = { `${config.URL_PREFIX}serviceLogin?client_id=${config.MH_CLIENT_ID}
                                                                    &redirect_uri=${window.location.href}&device_type=web&response_type=get` }
                                                                style = { styleWeb.signInButton }>
                                                                {t('prejoin.signinsignup')}
                                                            </a>
                                                        </li>
                                                    </ul>
                                                </div>
                                            )}
                                    </div>
                                    <br />
                                    <br />
                                </div>
                            </>
                        )}
                        {pregData && pregData.success && _isLobbyScreenVisible && (
                            <>
                                <div className = 'col-d-12 col-sm-12'>
                                    <br />
                                    <br />
                                    <div
                                        className = 'invalidMeeting text-center'
                                        style = { invalidMeetingStyle }>
                                        <div style = { stylePremeet.floatRight }>
                                            <Icon
                                                className = 'lobby-music'
                                                onClick = { this._onChangeIconState }
                                                src = { this.props._soundBool ? AudioOn : AudioOffMute } />
                                        </div>
                                        <img
                                            className = 'img-fluid'
                                            src = 'images/loading2.gif'
                                            style = { styleWeb.loadingImg } />
                                        <h4 style = { invalidMeetingTextStyle }>
                                            {_isLobbyScreenVisible && (
                                                _isPrejoin
                                                    ? t('welcomepage.pleaseWaitForTheStartMeeting', {
                                                        moderator: interfaceConfig?.CHANGE_MODERATOR_NAME || t('localRecording.moderator')
                                                    })
                                                    : this.props.participantMainCount >= this.props._meetingDetails?.max_participants
                                                        ? t('prejoin.oppsMaximumAllowedParticipantsErr')
                                                        : t('welcomepage.waitingInLobby', {
                                                            moderator: interfaceConfig?.CHANGE_MODERATOR_NAME || t('localRecording.moderator')
                                                        })

                                            )}
                                        </h4>

                                    </div>
                                    <br />
                                    <br />
                                </div>
                            </>
                        )}
                        {!pregData
                            && (!pregData?.success || pregData?.allow_guest === 0)
                            && !_jwt
                            && !_jwtStored && (
                            <>
                                <div className = 'col-md-12 col-sm-12'>
                                    <br />
                                    <br />
                                    <div
                                        className = 'invalidMeeting text-center'
                                        style = { invalidMeetingStyle }>
                                        <h4 style = { invalidMeetingTextStyle }>
                                            {t('welcomepage.oopsDeviceClockorTimezoneErr')}
                                        </h4>
                                    </div>
                                    <br />
                                    <br />
                                </div>
                            </>
                        )}
                        {pregData
                            && (pregData?.success === false || pregData?.allow_guest === 0)
                            && pregData.message === 'Invalid Credentials'
                            && _jwt
                            && _jwtStored && (
                            <>
                                <div className = 'col-md-12 col-sm-12'>
                                    <br />
                                    <br />
                                    <div
                                        className = 'invalidMeeting text-center'
                                        style = { invalidMeetingStyle }>
                                        <Icon
                                            className = 'img-fluid'
                                            src = { IconNotAllowed }
                                            style = { styleWeb.notAllowed } />
                                        <h4 style = { invalidMeetingTextStyle }>
                                            {t('welcomepage.oopsDeviceClockorTimezoneErr')}
                                        </h4>
                                    </div>
                                    <br />
                                    <br />
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* {this.props.footer} */}
                <Footer
                    footer = { this.props.footer }
                    prejoin = { true }
                    style = { styleDeeplinking(this.props.interfaceConfig).width100 } />
            </div>
        );
    }
}

/**
 * Maps state to the pros for premeeting screen.
 *
 * @param {*} state - State of the whole application.
 *
 * @returns {Object}
 */
export function mapStateToProps(state: Object) {
    const { mediaQueries } = state['features/base/responsive-ui'];
    const participants = state['features/base/participants'];
    const participant = participants.filter(i => i.id === 'local');
    const dialIn = state['features/invite'];
    const { userDetails } = state['features/base/mtApi'];
    const { lobbyScreen, soundBool } = state['features/lobby'];
    const { jwt, accessToken, jwtData, meetingDetails } = state['features/base/jwt'];
    const toolbarButtons = getToolbarButtons(state);
    const phoneNumber
        = dialIn && dialIn.numbers
            ? _getDefaultPhoneNumber(dialIn.numbers)
            : undefined;
    const { pregData } = state['features/base/mtApi'];

    const roomName = getBackendSafeRoomName(
        state['features/base/conference'].room
    );
    const DeviceSelectionProps = getDeviceSelectionDialogProps(state);
    const isParticipantKnockked = state['features/lobby'].knocking;


    // const stateJwt = state['features/base/jwt'];

    return {
        _smallDevices: mediaQueries,
        _localParticipant: participant,
        _phoneNumber: phoneNumber,
        _dialInVisible: isSharingEnabled(sharingFeatures.dialIn),
        _jwt: jwt,
        _toolbarButtons: toolbarButtons,
        _accessToken: accessToken,
        _userDetails: userDetails,
        pregData,
        roomName,
        _jwtStored: jwtData,
        _meetingDetails: meetingDetails,
        _meetingName: meetingDetails?.topic,
        _lobbyScreen: lobbyScreen,
        _soundBool: soundBool,
        _isParticipantKnockked: isParticipantKnockked,
        _deviceSelectionProps: DeviceSelectionProps
    };
}

/**
 * Maps state to the pros for premeeting screen.
 *
 * @param {*} dispatch - State of the whole application.
 *
 * @returns {Object}
 */
function mapDispatchToProps(dispatch: Function) {
    return {

        _playSound: param => dispatch(startSound(param)),
        _joinConference: () => dispatch(joinConference()),
        _watchConferenceInfo: (roomName, onUpdate, options) =>
            dispatch(watchConferenceInfo(roomName, onUpdate, options))
    };
}

export default translate(
    connect(mapStateToProps, mapDispatchToProps)(PreMeetingScreen)
);
